
import {
  FabricImage,
  FabricObject,
  Control,
  TPointerEvent,
  Transform,
} from "fabric";
// Fonction utilitaire pour ajouter les contrôles de recadrage à un objet
//
export function addCtrl(obj: FabricObject) {
  const sides = ["left", "right", "top", "bottom"] as const;
  const positions = {
    left: { x: -0.5, y: 0 },
    right: { x: 0.5, y: 0 },
    top: { x: 0, y: -0.5 },
    bottom: { x: 0, y: 0.5 },
  };

  // Associer chaque side à son control
  const controlNames = {
    left: "ml",
    right: "mr",
    top: "mt",
    bottom: "mb",
  };

  // Créer les contrôles pour chaque côté
  sides.forEach((side) => {
    const handler = createCropHandler(side);
    const position = positions[side];

    obj.controls[controlNames[side]] = new Control({
      x: position.x,
      y: position.y,
      actionHandler: handler.actionHandler,
      cursorStyleHandler: handler.cursorStyleHandler,
    });
  });

  //canvas.requestRenderAll();
  return obj;
}

type CropHandlerConfig = {
  dimension: "width" | "height";
  position: "left" | "top";
  crop: "cropX" | "cropY";
  anchor: "tr" | "tl" | "bl" | "br";
  sign: 1 | -1;
  defaultCursor: string;
};

function createCropHandler(side: "left" | "right" | "top" | "bottom") {
  return {
    actionHandler: function(eventData: TPointerEvent, transform: Transform) {
      const target = transform.target as FabricImage;
      const canvas = target.canvas;
      if (!canvas) return true;

      target.fire("scaling"); // on call scaling
      const configs: Record<typeof side, CropHandlerConfig> = {
        left: {
          dimension: "width",
          position: "left",
          crop: "cropX",
          anchor: "tr",
          sign: 1,
          defaultCursor: "w-resize",
        },
        right: {
          dimension: "width",
          position: "left",
          crop: "cropX",
          anchor: "tl",
          sign: -1,
          defaultCursor: "e-resize",
        },
        top: {
          dimension: "height",
          position: "top",
          crop: "cropY",
          anchor: "bl",
          sign: 1,
          defaultCursor: "n-resize",
        },
        bottom: {
          dimension: "height",
          position: "top",
          crop: "cropY",
          anchor: "tl",
          sign: -1,
          defaultCursor: "s-resize",
        },
      };

      const config = configs[side];

      // Sauvegarder le point d'ancrage
      const anchorPoint = target.aCoords?.[config.anchor];
      if (!anchorPoint) return true;

      // Obtenir le point du curseur dans les coordonnées de la scène
      const pointer = canvas.getScenePoint(eventData);

      // Calculer le delta en fonction du côté
      const currentPos = target[config.position] || 0;
      const currentDim = target[config.dimension] || 0;
      let delta;

      if (side === "left" || side === "top") {
        delta = (side === "left" ? pointer.x : pointer.y) - currentPos;
      } else {
        delta =
          side === "right"
            ? // un peu lourd mais pour gérer le scale...
            (currentPos + (currentDim * target.scaleX - pointer.x)) /
            target.scaleX
            : (currentPos + (currentDim * target.scaleY - pointer.y)) /
            target.scaleY;
      }

      // Calculer les nouvelles valeurs
      const currentCrop = target[config.crop] || 0;
      const newCrop =
        side === "left" || side === "top" ? currentCrop + delta : currentCrop;

      const newDimension =
        currentDim - delta * (side === "left" || side === "top" ? 1 : 1);
      // TODO:
      // quand on dépasse la taille, scale en même temps que crop

      // Vérifier si les nouvelles dimensions sont valides
      if (newDimension > 1 && newCrop >= 0) {
        // Appliquer les changements
        target[config.crop] = newCrop;
        target[config.dimension] = newDimension;

        // Recalculer la position pour maintenir le point d'ancrage fixe
        target.setCoords();
        const newAnchorPoint = target.aCoords?.[config.anchor];

        if (newAnchorPoint) {
          // Ajuster la position pour maintenir le point d'ancrage
          target.left += anchorPoint.x - newAnchorPoint.x;
          target.top += anchorPoint.y - newAnchorPoint.y;
          target.setCoords();
        }

        canvas.requestRenderAll();
      }
      return true;
    },

    // Ajout du gestionnaire de style de curseur directement ici
    cursorStyleHandler: function(_eventData: any, _control: any, fabricObject: FabricObject) {
      const angle = (fabricObject.angle || 0) % 180;

      // Déterminer si cet axe a été "inversé" par la rotation
      const isHorizontalControl = side === "left" || side === "right";
      const isVerticalControl = side === "top" || side === "bottom";

      // Si l'angle est entre 45° et 135°, les contrôles horizontaux deviennent verticaux et vice-versa
      if (angle >= 45 && angle <= 135) {
        return isHorizontalControl ? "ns-resize" : "ew-resize";
      } else {
        return isHorizontalControl ? "ew-resize" : "ns-resize";
      }
    },
  };
}
